import React, { useEffect, useState } from "react";
import { FiTrash2, FiX } from "react-icons/fi";

import { EntityHeader } from "./EntityHeader";
import { MappingSummary } from "./MappingSummary";
import { TableMappingsCard } from "./TableMappingsCard";
import { HubMetadataForm } from "./forms/HubMetadataForm";
import { SubjectAreaField } from "./forms/SubjectAreaField";
import { SatelliteMetadataForm } from "./forms/SatelliteMetadataForm";
import { LinkMetadataForm } from "./forms/LinkMetadataForm";
import { formatLabel } from "../model/formatting";
import { useDataSources } from "../../../hooks/useDataSources";
import { fetchSourceEntityColumnAutocomplete } from "../../../services/dataVaultModelling.service";
import type {
  DataVaultModel,
  SelectedEntity,
  TableRecord
} from "../model/types";
import {
  DISTRIBUTION_HASH,
  DISTRIBUTION_ROUND_ROBIN,
  DISTRIBUTION_TYPE_OPTIONS,
  getDistributionValidationError,
  isHashDistribution,
  isSqlPoolDedicated,
  normalizeDistributionType
} from "../../../helpers/distribution";
import { VirtualizationToggleCard } from "./VirtualizationToggleCard";
import { FlagToggleCard } from "./FlagToggleCard";
import { getConstructTypeForState, isVirtualizedConstruct } from "../model/constructs";
import type { SqlAutocompleteContext } from "./forms/SqlEditorField";
import { MetadataActionFooter } from "../../../components/modelling/MetadataActionFooter";

const isFlagEnabled = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === "y" || normalized === "yes" || normalized === "true";
};

const toFlagString = (value: boolean): string => (value ? "Y" : "");

/**
 * Warehouse target that actually applies to an entity: the workspace-level value when
 * known, otherwise the environment values echoed on the record (on-prem wins, matching
 * the backend resolver).
 */
const resolveTargetType = (
  record: TableRecord,
  workspaceTargetType: string | null | undefined
): string | null | undefined =>
  workspaceTargetType ||
  (record.dw_target_types_onprem ?? "").trim() ||
  record.dw_target_types;

const isFabricTarget = (value: string | null | undefined): boolean => {
  const v = (value ?? "").toString().trim().toUpperCase();
  return v === "FABRIC_DW" || v === "FABRIC_LAKEHOUSE";
};


export function EntityDetailsPanel({
  entity,
  model,
  onOpenMappings,
  onHubFieldChange,
  onManualFieldChange,
  onSatelliteFieldChange,
  onSatelliteParentChange,
  onLinkHubsChange,
  onLinkFieldChange,
  onRemoveHub,
  onRemoveSatellite,
  onRemoveLink,
  onRemoveManual,
  onSave,
  onCancel,
  canSave,
  isSaving,
  isDirty,
  errorMessage,
  successMessage,
  subjectAreaOptions,
  onAddSubjectArea,
  dwTargetType
}: {
  entity: Exclude<SelectedEntity, null>;
  model: DataVaultModel;
  onOpenMappings: () => void;
  onHubFieldChange: (hubId: string, field: keyof TableRecord, value: string) => void;
  onManualFieldChange: (manualId: string, field: keyof TableRecord, value: string) => void;
  onSatelliteFieldChange: (satelliteId: string, field: keyof TableRecord, value: string) => void;
  onSatelliteParentChange: (satelliteId: string, parentHubId: string | null) => void;
  /** Hub ids for a link, IN THE ORDER CHOSEN — the first drives naming. */
  onLinkHubsChange: (linkId: string, hubIds: string[]) => void;
  onLinkFieldChange: (linkId: string, field: keyof TableRecord, value: string) => void;
  onRemoveHub: (hubId: string) => void;
  onRemoveSatellite: (satelliteId: string) => void;
  onRemoveLink: (linkId: string) => void;
  onRemoveManual: (manualId: string) => void;
  onSave: () => void;
  onCancel: () => void;
  canSave: boolean;
  isSaving: boolean;
  isDirty: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  subjectAreaOptions: string[];
  onAddSubjectArea: (value: string) => Promise<{ ok: boolean; value: string; error?: string }>;
  /**
   * Effective warehouse target for the active environment (on-prem overrides cloud).
   * The per-entity `dw_target_types` echoes only the cloud value, so Aleris — cloud
   * SQLPOOL_DEDICATED, on-prem LAKEHOUSE — used to see distribution controls that
   * generation ignores.
   */
  dwTargetType?: string | null;
}): JSX.Element {
  const environmentIdRaw =
    typeof window !== "undefined" ? window.localStorage.getItem("environment_id") : null;
  const environmentId = environmentIdRaw ? Number(environmentIdRaw) : null;
  const {
    dataSources,
    loading: dataSourcesLoading,
    error: dataSourcesError
  } = useDataSources(environmentId);
  const [controlPanelOpen, setControlPanelOpen] = useState(false);
  const [sqlAutocomplete, setSqlAutocomplete] = useState<SqlAutocompleteContext>({
    sourceColumns: [],
    targetColumns: []
  });

  useEffect(() => {
    setControlPanelOpen(false);
  }, [entity.entity.id]);

  useEffect((): (() => void) | undefined => {
    const sourceEntityId = entity.entity.record.source_entity_id
      ? Number(entity.entity.record.source_entity_id)
      : null;
    const targetEntityRdvId = entity.entity.record.target_entity_rdv_id
      ? Number(entity.entity.record.target_entity_rdv_id)
      : null;
    const customerIdRaw =
      typeof window !== "undefined" ? window.localStorage.getItem("customer_id") : null;
    const customerId = customerIdRaw ? Number(customerIdRaw) : null;

    setSqlAutocomplete({
      sourceEntityName: entity.entity.record.source_entity_name ?? undefined,
      targetEntityName:
        entity.entity.record.target_entity_rdv_name || entity.entity.record.target_table || undefined,
      sourceColumns: [],
      targetColumns: []
    });

    if (!environmentId || !sourceEntityId) {
      return undefined;
    }

    let isCancelled = false;

    fetchSourceEntityColumnAutocomplete({
      source_entity_id: sourceEntityId,
      environment_id: environmentId,
      customer_id: customerId ?? undefined,
      target_entity_rdv_id: targetEntityRdvId ?? undefined
    })
      .then((response) => {
        if (isCancelled) {
          return;
        }
        setSqlAutocomplete({
          sourceEntityName: entity.entity.record.source_entity_name ?? undefined,
          targetEntityName:
            entity.entity.record.target_entity_rdv_name || entity.entity.record.target_table || undefined,
          sourceColumns: response.source_entity_column_names ?? [],
          targetColumns: response.target_entity_rdv_column_names ?? []
        });
      })
      .catch((error) => {
        console.error("Unable to load SQL autocomplete metadata", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    entity.entity.id,
    entity.entity.record.source_entity_id,
    entity.entity.record.source_entity_name,
    entity.entity.record.target_entity_rdv_id,
    entity.entity.record.target_entity_rdv_name,
    entity.entity.record.target_table,
    environmentId
  ]);

  const renderControlActions = (
    generateModelEnabled: boolean,
    onGenerateToggle: (next: boolean) => void,
    customView?: {
      show: boolean;
      enabled: boolean;
      onToggle: (next: boolean) => void;
    }
  ) => (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-2.5">
      <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3.5">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-slate-500">
              Generation
            </p>
            <p className="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-700">
              Generate model entity
            </p>
          </div>
          <button
            type="button"
            onClick={() => onGenerateToggle(!generateModelEnabled)}
            aria-pressed={generateModelEnabled}
            className={`tw-relative tw-inline-flex tw-h-7 tw-w-14 tw-shrink-0 tw-items-center tw-rounded-full tw-transition ${
              generateModelEnabled ? "tw-bg-[#03234a]" : "tw-bg-slate-300"
            }`}
          >
            <span
              className={`tw-inline-block tw-h-6 tw-w-6 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-transition ${
                generateModelEnabled ? "tw-translate-x-7" : "tw-translate-x-1"
              }`}
            />
          </button>
        </div>
        <p className="tw-mt-1.5 tw-pr-2 tw-text-xs tw-leading-5 tw-text-slate-500">
          Include this entity when generating downstream model artifacts.
        </p>
      </div>
      {customView?.show ? (
        <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3.5">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <div className="tw-min-w-0">
              <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-slate-500">
                Fabric
              </p>
              <p className="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-700">
                Custom view / table
              </p>
            </div>
            <button
              type="button"
              onClick={() => customView.onToggle(!customView.enabled)}
              aria-pressed={customView.enabled}
              className={`tw-relative tw-inline-flex tw-h-7 tw-w-14 tw-shrink-0 tw-items-center tw-rounded-full tw-transition ${
                customView.enabled ? "tw-bg-[#03234a]" : "tw-bg-slate-300"
              }`}
            >
              <span
                className={`tw-inline-block tw-h-6 tw-w-6 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-transition ${
                  customView.enabled ? "tw-translate-x-7" : "tw-translate-x-1"
                }`}
              />
            </button>
          </div>
          <p className="tw-mt-1.5 tw-pr-2 tw-text-xs tw-leading-5 tw-text-slate-500">
            Read the source locally in the warehouse instead of through a lakehouse
            reference (no [lakehouse]. prefix in the generated SQL).
          </p>
        </div>
      ) : null}
      <div className="tw-w-full">
        <button
          type="button"
          onClick={() => setControlPanelOpen(true)}
          className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-border-slate-400 hover:tw-bg-slate-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw:ring-slate-300"
        >
          Advanced settings
        </button>
      </div>
    </div>
  );

  if (entity.type === "hub") {
    const hub = entity.entity;
    const linkedSatellites = model.satellites.filter(
      (satellite) => satellite.parentHubId === hub.id
    ).length;
    const linkedLinks = model.links.filter((link) =>
      link.connectedHubIds.includes(hub.id)
    ).length;

    const isVirtualized = isVirtualizedConstruct(hub.record.datavault_construct_type);
    const includeDqEnabled = isFlagEnabled(hub.record.include_dq_test);
    const generateModelEnabled = isFlagEnabled(hub.record.generate_model_entity);
    // Virtualized constructs are generated as CREATE VIEW, which never carries a
    // DISTRIBUTION clause — only *AsTable goes through the CTAS path that does.
    const showDistributionSettings =
      !isVirtualized && isSqlPoolDedicated(resolveTargetType(hub.record, dwTargetType));
    return (
      <>
        <EntityControlPanel
          open={controlPanelOpen}
          onClose={() => setControlPanelOpen(false)}
          entityLabel={formatLabel(hub.record.target_table) || "Hub"}
          isPersisted={!isVirtualized}
          onPersistToggle={(nextPersisted) =>
            onHubFieldChange(
              hub.id,
              "datavault_construct_type",
              getConstructTypeForState("hub", !nextPersisted)
            )
          }
          includeDqEnabled={includeDqEnabled}
          onIncludeToggle={(next) => onHubFieldChange(hub.id, "include_dq_test", toFlagString(next))}
        />
        <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-5">
          <EntityHeader
            title={formatLabel(hub.record.target_table) || "Untitled hub"}
            chip="Hub"
            description={hub.record.Description || "Business key anchor for the ensemble."}
            accent="blue"
            actionSlot={renderControlActions(
              generateModelEnabled,
              (next) =>
                onHubFieldChange(hub.id, "generate_model_entity", toFlagString(next)),
              {
                show: isFabricTarget(hub.record.dw_target_types),
                enabled: isFlagEnabled(hub.record.is_custom_view_or_table),
                onToggle: (next) =>
                  onHubFieldChange(
                    hub.id,
                    "is_custom_view_or_table",
                    toFlagString(next)
                  )
              }
            )}
          />
          <MappingSummary entity={entity} onOpenMappings={onOpenMappings} />
          <HubMetadataForm
            hub={hub}
            onFieldChange={(field, value) => onHubFieldChange(hub.id, field, value)}
            linkedSatelliteCount={linkedSatellites}
            linkedLinkCount={linkedLinks}
            dataSources={dataSources}
            dataSourcesLoading={dataSourcesLoading}
            dataSourcesError={dataSourcesError}
            subjectAreaOptions={subjectAreaOptions}
            onAddSubjectArea={onAddSubjectArea}
            sqlAutocomplete={sqlAutocomplete}
          />
          <TableMappingsCard mappings={hub.mappings} />
          {showDistributionSettings ? (
            <DistributionSettings
              distributionType={hub.record.distribution_type}
              distributionKey={hub.record.distribution_key ?? ""}
              onTypeChange={(value) => onHubFieldChange(hub.id, "distribution_type", value)}
              onKeyChange={(value) => onHubFieldChange(hub.id, "distribution_key", value)}
            />
          ) : null}
          <EntityActionButtons
            onSave={onSave}
            canSave={canSave}
            isSaving={isSaving}
            isDirty={isDirty}
            errorMessage={errorMessage}
            successMessage={successMessage}
          />
          <EntityDangerZone
            label="Delete Hub"
            description="Removing this hub detaches satellites and updates any connected links."
            onDelete={() => onRemoveHub(hub.id)}
          />
        </div>
      </>
    );
  }

  if (entity.type === "satellite") {
    const satellite = entity.entity;
    const isVirtualized = isVirtualizedConstruct(satellite.record.datavault_construct_type);
    const isConnectedToHub = Boolean(satellite.parentHubId);
    const includeDqEnabled = isFlagEnabled(satellite.record.include_dq_test);
    const generateModelEnabled = isFlagEnabled(satellite.record.generate_model_entity);
    const showDistributionSettings =
      !isVirtualized &&
      isSqlPoolDedicated(resolveTargetType(satellite.record, dwTargetType));
    return (
      <>
        <EntityControlPanel
          open={controlPanelOpen}
          onClose={() => setControlPanelOpen(false)}
          entityLabel={formatLabel(satellite.record.target_table) || "Satellite"}
          isPersisted={!isVirtualized}
          onPersistToggle={(nextPersisted) =>
            onSatelliteFieldChange(
              satellite.id,
              "datavault_construct_type",
              getConstructTypeForState("satellite", !nextPersisted)
            )
          }
          includeDqEnabled={includeDqEnabled}
          onIncludeToggle={(next) =>
            onSatelliteFieldChange(satellite.id, "include_dq_test", toFlagString(next))
          }
        />
        <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-5">
          <EntityHeader
            title={formatLabel(satellite.record.target_table) || "Untitled satellite"}
            chip="Satellite"
            description={
              satellite.record.Description ||
              "Captures descriptive attributes and historical changes."
            }
            accent="rose"
            actionSlot={renderControlActions(
              generateModelEnabled,
              (next) =>
                onSatelliteFieldChange(
                  satellite.id,
                  "generate_model_entity",
                  toFlagString(next)
                ),
              {
                show: isFabricTarget(satellite.record.dw_target_types),
                enabled: isFlagEnabled(satellite.record.is_custom_view_or_table),
                onToggle: (next) =>
                  onSatelliteFieldChange(
                    satellite.id,
                    "is_custom_view_or_table",
                    toFlagString(next)
                  )
              }
            )}
          />
          <MappingSummary
            entity={entity}
            onOpenMappings={onOpenMappings}
            disabled={!isConnectedToHub}
          />
          <SatelliteMetadataForm
            satellite={satellite}
            hubs={model.hubs}
            onFieldChange={(field, value) => onSatelliteFieldChange(satellite.id, field, value)}
            onParentChange={(parentHubId) => onSatelliteParentChange(satellite.id, parentHubId)}
            dataSources={dataSources}
            dataSourcesLoading={dataSourcesLoading}
            dataSourcesError={dataSourcesError}
            sqlAutocomplete={sqlAutocomplete}
          />
          <TableMappingsCard mappings={satellite.mappings} />
          {showDistributionSettings ? (
            <DistributionSettings
              distributionType={satellite.record.distribution_type}
              distributionKey={satellite.record.distribution_key ?? ""}
              onTypeChange={(value) => onSatelliteFieldChange(satellite.id, "distribution_type", value)}
              onKeyChange={(value) => onSatelliteFieldChange(satellite.id, "distribution_key", value)}
            />
          ) : null}
          <EntityActionButtons
            onSave={onSave}
            canSave={canSave}
            isSaving={isSaving}
            isDirty={isDirty}
            errorMessage={errorMessage}
            successMessage={successMessage}
          />
          <EntityDangerZone
            label="Delete Satellite"
            description="This removes the satellite and any link associations that reference it."
            onDelete={() => onRemoveSatellite(satellite.id)}
          />
        </div>
      </>
    );
  }

  if (entity.type === "manual") {
    const manualEntity = entity.entity;
    const inputClass =
      "tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100";
    return (
      <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-5">
        <section className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-5">
          <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-500/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white">
            Manual
          </span>
          <h3 className="tw-mt-2 tw-font-mono tw-text-sm tw-font-semibold tw-text-slate-900 tw-break-words">
            {manualEntity.record.target_schema
              ? `${manualEntity.record.target_schema}.${manualEntity.record.target_table}`
              : manualEntity.record.target_table}
          </h3>
          <p className="tw-mt-2 tw-text-[13px] tw-text-slate-600">
            Manually maintained / business-data-vault entity. Not generated as a standard hub,
            satellite, or link. Available as a source for downstream mappings.
          </p>
        </section>

        {/* Editable settings — these were previously locked, leaving no way to correct a
            mis-assigned subject area or description after creation. */}
        <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-5">
          <h4 className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
            Settings
          </h4>
          <SubjectAreaField
            value={manualEntity.record.subject_area ?? ""}
            options={subjectAreaOptions}
            onChange={(value) => onManualFieldChange(manualEntity.id, "subject_area", value)}
            onAddSubjectArea={onAddSubjectArea}
          />
          <label className="tw-flex tw-flex-col tw-gap-1.5">
            <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              Description
            </span>
            <textarea
              value={manualEntity.record.Description ?? ""}
              onChange={(event) =>
                onManualFieldChange(manualEntity.id, "Description", event.target.value)
              }
              rows={3}
              placeholder="Optional description"
              className={inputClass}
            />
          </label>
        </section>

        <TableMappingsCard mappings={manualEntity.mappings} />

        <EntityDangerZone
          label="Delete entity"
          description="Removes this manual/legacy entity from the Raw Data Vault. Re-model it as a hub, satellite, or link if it should be generated."
          onDelete={() => onRemoveManual(manualEntity.id)}
        />
      </div>
    );
  }

  const link = entity.entity;
  const isVirtualized = isVirtualizedConstruct(link.record.datavault_construct_type);
  const includeDqEnabled = isFlagEnabled(link.record.include_dq_test);
  const generateModelEnabled = isFlagEnabled(link.record.generate_model_entity);
  const showDistributionSettings =
    !isVirtualized && isSqlPoolDedicated(resolveTargetType(link.record, dwTargetType));

  return (
    <>
      <EntityControlPanel
        open={controlPanelOpen}
        onClose={() => setControlPanelOpen(false)}
        entityLabel={formatLabel(link.record.target_table) || "Link"}
        isPersisted={!isVirtualized}
        onPersistToggle={(nextPersisted) =>
          onLinkFieldChange(
            link.id,
            "datavault_construct_type",
            getConstructTypeForState("link", !nextPersisted)
          )
        }
        includeDqEnabled={includeDqEnabled}
        onIncludeToggle={(next) => onLinkFieldChange(link.id, "include_dq_test", toFlagString(next))}
      />
        <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-5">
          <EntityHeader
            title={formatLabel(link.record.target_table) || "Untitled link"}
            chip="Link"
            description={
              link.record.Description ??
              "Associative structure connecting multiple business hubs."
            }
            accent="sky"
            actionSlot={renderControlActions(
              generateModelEnabled,
              (next) =>
                onLinkFieldChange(link.id, "generate_model_entity", toFlagString(next)),
              {
                show: isFabricTarget(link.record.dw_target_types),
                enabled: isFlagEnabled(link.record.is_custom_view_or_table),
                onToggle: (next) =>
                  onLinkFieldChange(
                    link.id,
                    "is_custom_view_or_table",
                    toFlagString(next)
                  )
              }
            )}
          />
        <MappingSummary entity={entity} onOpenMappings={onOpenMappings} />
        <LinkMetadataForm
          link={link}
          hubs={model.hubs}
          satellites={model.satellites}
          onFieldChange={(field, value) => onLinkFieldChange(link.id, field, value)}
          onHubsChange={(hubIds) => onLinkHubsChange(link.id, hubIds)}
          excludeNullBusinessKeys={isFlagEnabled(link.record.exclude_null_business_keys)}
          onExcludeNullBusinessKeysChange={(next) =>
            onLinkFieldChange(link.id, "exclude_null_business_keys", next ? "true" : "false")
          }
          dataSources={dataSources}
          dataSourcesLoading={dataSourcesLoading}
          dataSourcesError={dataSourcesError}
          subjectAreaOptions={subjectAreaOptions}
          onAddSubjectArea={onAddSubjectArea}
          sqlAutocomplete={sqlAutocomplete}
        />
        <TableMappingsCard mappings={link.mappings} />
        {showDistributionSettings ? (
          <DistributionSettings
            distributionType={link.record.distribution_type}
            distributionKey={link.record.distribution_key ?? ""}
            onTypeChange={(value) => onLinkFieldChange(link.id, "distribution_type", value)}
            onKeyChange={(value) => onLinkFieldChange(link.id, "distribution_key", value)}
          />
        ) : null}
        <EntityActionButtons
          onSave={onSave}
          canSave={canSave}
          isSaving={isSaving}
          isDirty={isDirty}
          errorMessage={errorMessage}
          successMessage={successMessage}
        />
        <EntityDangerZone
          label="Delete Link"
          description="Removes this relationship and detaches any connected hubs or satellites."
          onDelete={() => onRemoveLink(link.id)}
        />
      </div>
    </>
  );
}

function EntityControlPanel({
  open,
  onClose,
  entityLabel,
  isPersisted,
  onPersistToggle,
  includeDqEnabled,
  onIncludeToggle
}: {
  open: boolean;
  onClose: () => void;
  entityLabel: string;
  isPersisted: boolean;
  onPersistToggle: (next: boolean) => void;
  includeDqEnabled: boolean;
  onIncludeToggle: (next: boolean) => void;
}): JSX.Element {
  return (
    <>
      <div
        className={`tw-fixed tw-inset-y-0 tw-right-0 tw-z-50 tw-w-full tw-max-w-sm tw-transform tw-bg-white tw-shadow-2xl tw-transition-transform tw-duration-300 ${
          open ? "tw-translate-x-0" : "tw-translate-x-full"
        }`}
      >
        <div className="tw-flex tw-h-full tw-flex-col tw-border-l tw-border-slate-100 tw-bg-white tw-p-6">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="tw-text-[0.8rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-400">
                Entity controls
              </p>
              <h4 className="tw-text-base tw-font-semibold tw-text-slate-900">{entityLabel}</h4>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-text-slate-500 tw-transition hover:tw-bg-slate-100"
            >
              <FiX className="tw-h-4 tw-w-4" />
            </button>
          </div>
          <div className="tw-mt-6 tw-space-y-4">
            <VirtualizationToggleCard isPersisted={isPersisted} onToggle={onPersistToggle} />
            <FeatureToggleSection
              includeDqEnabled={includeDqEnabled}
              onIncludeToggle={onIncludeToggle}
            />
          </div>
        </div>
      </div>
      <div
        className={`tw-fixed tw-inset-0 tw-z-40 tw-bg-slate-900/30 tw-backdrop-blur-sm tw-transition-opacity ${
          open ? "tw-opacity-100 tw-pointer-events-auto" : "tw-opacity-0 tw-pointer-events-none"
        }`}
        onClick={onClose}
      />
    </>
  );
}

function FeatureToggleSection({
  includeDqEnabled,
  onIncludeToggle
}: {
  includeDqEnabled: boolean;
  onIncludeToggle: (next: boolean) => void;
}): JSX.Element {
  return (
    <div className="tw-space-y-3">
      <FlagToggleCard
        label="Include DQ Tests"
        value={includeDqEnabled}
        onToggle={onIncludeToggle}
      />
    </div>
  );
}

function EntityDangerZone({
  label,
  description,
  onDelete
}: {
  label: string;
  description: string;
  onDelete: () => void;
}): JSX.Element {
  return (
    <section className="tw-rounded-2xl tw-border tw-border-rose-200 tw-bg-rose-50/70 tw-p-5">
      <h3 className="tw-text-sm tw-font-semibold tw-text-rose-600">Danger zone</h3>
      <p className="tw-mt-2 tw-text-xs tw-text-rose-500">{description}</p>
      <button
        type="button"
        onClick={onDelete}
        className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-rose-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-rose-600 tw-transition hover:tw-bg-rose-100"
      >
        <FiTrash2 size={16} />
        {label}
      </button>
    </section>
  );
}

function EntityActionButtons({
  onSave,
  canSave,
  isSaving,
  isDirty,
  errorMessage,
  successMessage
}: {
  onSave: () => void;
  canSave: boolean;
  isSaving: boolean;
  isDirty: boolean;
  errorMessage: string | null;
  successMessage: string | null;
}): JSX.Element {
  return (
    <MetadataActionFooter
      onSave={onSave}
      canSave={canSave}
      isSaving={isSaving}
      isDirty={isDirty}
      errorMessage={errorMessage}
      successMessage={successMessage}
      showSaveButton={false}
    />
  );
}

function DistributionSettings({
  distributionType,
  distributionKey,
  onTypeChange,
  onKeyChange
}: {
  distributionType: string | null | undefined;
  distributionKey: string | null | undefined;
  onTypeChange: (value: string) => void;
  onKeyChange: (value: string) => void;
}): JSX.Element {
  // Unset falls back to ROUND_ROBIN (what the SQL pool itself defaults to), but a
  // stored value now wins — the blade used to show ROUND_ROBIN for every entity.
  const resolvedType = normalizeDistributionType(distributionType) || DISTRIBUTION_ROUND_ROBIN;
  const requiresKey = isHashDistribution(resolvedType);
  const keyError = getDistributionValidationError(resolvedType, distributionKey);
  // A stored-but-not-selectable value (legacy "N/A" on non-generated entities) still has
  // to be shown, or the select would silently display a different type than is stored.
  const typeOptions = DISTRIBUTION_TYPE_OPTIONS.some(
    (option) => option.value === resolvedType
  )
    ? DISTRIBUTION_TYPE_OPTIONS
    : [...DISTRIBUTION_TYPE_OPTIONS, { label: resolvedType, value: resolvedType }];
  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value;
    onTypeChange(nextValue);
    // The key only means something for HASH; keep it out of the payload otherwise.
    if (nextValue !== DISTRIBUTION_HASH) {
      onKeyChange("");
    }
  };

  return (
    <section className="tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-slate-50 tw-p-4">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
            Distribution type{" "}
            <span className="tw-text-[0.55rem] tw-font-normal tw-normal-case tw-text-slate-400">
              (Synapse SQL pool only)
            </span>
          </label>
          <select
            value={resolvedType}
            onChange={handleTypeChange}
            className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 focus:tw-border-[#0f172a] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#0f172a]/10"
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {requiresKey ? (
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
              Distribution key <span className="tw-text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={distributionKey ?? ""}
              onChange={(event) => onKeyChange(event.target.value)}
              placeholder="Enter hash expression or target column"
              aria-invalid={Boolean(keyError)}
              className={`tw-w-full tw-rounded-xl tw-border tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 ${
                keyError
                  ? "tw-border-rose-400 focus:tw-border-rose-500 focus:tw-ring-rose-500/10"
                  : "tw-border-slate-200 focus:tw-border-[#0f172a] focus:tw-ring-[#0f172a]/10"
              }`}
            />
            {keyError ? (
              <p className="tw-text-xs tw-font-medium tw-text-rose-600">{keyError}</p>
            ) : null}
          </div>
        ) : (
          <p className="tw-text-xs tw-text-slate-500">
            {resolvedType === DISTRIBUTION_ROUND_ROBIN
              ? "Round robin distribution does not require a distribution key."
              : `${resolvedType} distribution does not require a distribution key.`}
          </p>
        )}
      </div>
    </section>
  );
}
